<template>
  <div class="user-import-modal modal-card bg-white" :class="{ 'is-loading': loading }">
    <header class="user-import-modal__header">
      <div>
        <h2>Importer une liste d’utilisateurs</h2>
        <p>Ajoutez, réactivez ou désactivez plusieurs accès à partir d’un fichier CSV.</p>
      </div>
      <button type="button" aria-label="Fermer" :disabled="loading" @click="close">
        <i class="fas fa-times" />
      </button>
    </header>

    <form
      id="user-import-form"
      class="user-import-modal__content"
      :aria-busy="loading ? 'true' : 'false'"
      @submit.prevent="submitImport"
    >
      <section class="user-import-file-section">
        <div class="user-import-section-heading">
          <div>
            <h3>Fichier à importer</h3>
            <p>Format CSV, avec un séparateur point-virgule ou virgule.</p>
          </div>
          <button type="button" class="user-import-template-button" :disabled="loading" @click="downloadTemplate">
            <i class="fas fa-download" />
            <span>Télécharger le modèle</span>
          </button>
        </div>

        <label class="user-import-file" :class="{ 'has-file': csvFile }" for="csvFile">
          <input
            id="csvFile"
            ref="csvFile"
            class="user-import-file__input"
            type="file"
            accept=".csv,text/csv"
            required
            :disabled="loading"
            @change="handleFileChange"
          >
          <span class="user-import-file__icon">
            <i :class="csvFile ? 'fas fa-check-circle' : 'fas fa-file-csv'" />
          </span>
          <span class="user-import-file__copy">
            <strong>{{ csvFile ? csvFile.name : 'Sélectionner un fichier CSV' }}</strong>
            <small>{{ csvFile ? formatFileSize(csvFile.size) : 'Un seul fichier, au format .csv' }}</small>
          </span>
          <span class="user-import-file__action">Parcourir</span>
        </label>
      </section>

      <section class="user-import-format">
        <div class="user-import-format__title">
          <i class="fas fa-info-circle" aria-hidden="true" />
          <h3>Format attendu</h3>
        </div>
        <code>Nom; Prénom; Email; Statut; Groupe; Matricule</code>
        <ul>
          <li>Les en-têtes sont facultatifs. Sans en-têtes, les colonnes sont lues dans l’ordre indiqué.</li>
          <li><strong>IN</strong> nécessite un e-mail. <strong>OUT</strong> nécessite un e-mail ou un matricule.</li>
          <li>Un même matricule peut apparaître sur plusieurs lignes.</li>
        </ul>
      </section>

      <label class="user-import-mail-option" for="sendMail">
        <input id="sendMail" v-model="sendMail" type="checkbox" :disabled="loading">
        <span>
          <strong>Envoyer un mail de notification</strong>
          <small>Les utilisateurs ajoutés seront informés par e-mail.</small>
        </span>
      </label>

      <section v-if="importErrors.length || importErrorMessage" class="user-import-errors" role="alert">
        <div>
          <i class="fas fa-exclamation-circle" aria-hidden="true" />
          <strong>{{ importErrorMessage || 'Certaines lignes ne peuvent pas être importées.' }}</strong>
        </div>
        <ul v-if="importErrors.length">
          <li v-for="(error, index) in importErrors" :key="index">
            {{ formatImportError(error) }}
          </li>
        </ul>
      </section>
    </form>

    <footer class="user-import-modal__footer">
      <button type="button" class="user-import-cancel" :disabled="loading" @click="close">
        Annuler
      </button>
      <button
        type="submit"
        form="user-import-form"
        class="user-import-submit"
        :disabled="loading || !csvFile"
      >
        <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-file-import'" />
        <span>{{ loading ? 'Import en cours…' : 'Importer la liste' }}</span>
      </button>
    </footer>
  </div>
</template>

<script>
import { http } from '@/services/api.service'

export default {
  name: 'UserCsvImportModal',
  data () {
    return {
      loading: false,
      csvFile: null,
      sendMail: false,
      importErrors: [],
      importErrorMessage: ''
    }
  },
  methods: {
    handleFileChange (event) {
      const file = event.target.files[0]
      if (file) {
        this.csvFile = file
        this.importErrors = []
        this.importErrorMessage = ''
      }
    },
    formatImportError (error) {
      const parts = []
      if (error.row) {
        parts.push(`Ligne ${error.row}`)
      }
      if (error.field) {
        parts.push(`champ ${error.field}`)
      }
      const prefix = parts.length ? `${parts.join(' - ')} : ` : ''
      const message = error.message || 'Erreur'
      const value = error.value ? ` (valeur: ${error.value})` : ''
      return `${prefix}${message}${value}`
    },
    formatFileSize (size) {
      if (!Number.isFinite(size) || size <= 0) return 'Fichier CSV sélectionné'
      if (size < 1024) return `${size} octets`
      return `${(size / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Ko`
    },
    downloadTemplate () {
      const rows = [
        ['Nom', 'Prénom', 'Email', 'Statut', 'Groupe', 'Matricule'],
        ['Dupont', 'Jean', 'jean.dupont@example.com', 'IN', 'Groupe A', '12345'],
        ['Dupont', 'Jean', 'jean.dupont.alt@example.com', 'IN', 'Groupe A', '12345'],
        ['', '', 'jean.dupont@example.com', 'OUT', '', '']
      ]
      const csv = rows.map(row => row.map(this.escapeCsvValue).join(';')).join('\r\n')
      const blob = new Blob([`\uFEFF${csv}\r\n`], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'modele-import-utilisateurs.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    escapeCsvValue (value) {
      const stringValue = String(value)
      if (/[;"\r\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    },
    async submitImport () {
      if (!this.csvFile) {
        this.$notify({
          group: 'maviepro-error',
          text: 'Veuillez sélectionner un fichier CSV.'
        })
        return
      }

      if (this.loading) return

      this.importErrors = []
      this.importErrorMessage = ''
      this.loading = true
      const formData = new FormData()
      formData.append('file', this.csvFile)
      formData.append('send_mail', this.sendMail ? '1' : '0')

      try {
        const { data } = await http.post('/user/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.importErrors = []
        this.importErrorMessage = ''
        this.$emit('imported')
        this.$notify({
          group: 'maviepro-success',
          text: data.message
        })
        this.close()
      } catch (err) {
        const response = err && err.response ? err.response.data : null
        if (response) {
          if (Array.isArray(response.errors)) {
            this.importErrors = response.errors
            this.importErrorMessage = response.message || 'Import invalide.'
          } else if (response.errors && typeof response.errors === 'object') {
            this.importErrors = Object.entries(response.errors).flatMap(([field, messages]) =>
              messages.map(message => ({ field, message }))
            )
            this.importErrorMessage = response.message || 'Erreur de validation.'
          } else if (response.error) {
            this.importErrorMessage = response.error
          } else if (response.message) {
            this.importErrorMessage = response.message
          }
        }
        this.$notify({
          group: 'maviepro-error',
          text: this.importErrorMessage || "Erreur lors de l'import des utilisateurs. Veuillez réessayer."
        })
      } finally {
        this.loading = false
      }
    },
    close () {
      this.$parent.close()
    }
  }
}
</script>

<style scoped>
.user-import-modal {
  display: flex;
  width: min(44rem, calc(100vw - 2rem));
  max-height: calc(100vh - 3rem);
  flex-direction: column;
  overflow: hidden;
  border-radius: 1rem;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
}

.user-import-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.user-import-modal__header h2 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
}

.user-import-modal__header p {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.4;
}

.user-import-modal__header > button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  color: #64748b;
}

.user-import-modal__header > button:hover:not(:disabled) {
  background: #f8fafc;
  color: #111827;
}

.user-import-modal__content {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  padding: 1.5rem;
}

.user-import-file-section {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
}

.user-import-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.user-import-section-heading h3,
.user-import-format h3 {
  color: #111827;
  font-size: 0.875rem;
  font-weight: 700;
}

.user-import-section-heading p {
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.75rem;
}

.user-import-template-button {
  display: inline-flex;
  min-height: 2.25rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  gap: 0.45rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #fff;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 700;
}

.user-import-template-button:hover:not(:disabled) {
  border-color: #93c5fd;
  background: #eff6ff;
}

.user-import-file {
  position: relative;
  display: flex;
  min-height: 5.5rem;
  align-items: center;
  gap: 0.85rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px dashed #93c5fd;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.user-import-file:hover,
.user-import-file:focus-within {
  border-color: #2563eb;
  background: #f8fbff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.user-import-file.has-file {
  border-style: solid;
  border-color: #86efac;
  background: #f0fdf4;
}

.user-import-file__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.user-import-file__icon {
  display: inline-flex;
  width: 2.75rem;
  height: 2.75rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 9999px;
  background: #dbeafe;
  color: #2563eb;
  font-size: 1.15rem;
}

.user-import-file.has-file .user-import-file__icon {
  background: #dcfce7;
  color: #15803d;
}

.user-import-file__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.user-import-file__copy strong {
  overflow: hidden;
  color: #334155;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-import-file__copy small {
  margin-top: 0.2rem;
  color: #94a3b8;
  font-size: 0.75rem;
}

.user-import-file__action {
  flex: 0 0 auto;
  padding: 0.4rem 0.65rem;
  border-radius: 7px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 700;
}

.user-import-format {
  padding: 1rem;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  background: #eff6ff;
  color: #1e3a8a;
}

.user-import-format__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.user-import-format__title i {
  color: #2563eb;
}

.user-import-format code {
  display: block;
  margin-top: 0.7rem;
  padding: 0.65rem 0.75rem;
  overflow-x: auto;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #fff;
  color: #1e3a8a;
  font-family: monospace;
  font-size: 0.75rem;
  white-space: nowrap;
}

.user-import-format ul {
  margin: 0.75rem 0 0;
  padding-left: 1.1rem;
  color: #475569;
  font-size: 0.75rem;
  line-height: 1.5;
  list-style: disc;
}

.user-import-format li + li {
  margin-top: 0.3rem;
}

.user-import-mail-option {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
}

.user-import-mail-option:hover {
  background: #f8fafc;
}

.user-import-mail-option input {
  width: 1rem;
  height: 1rem;
  margin-top: 0.15rem;
  flex: 0 0 auto;
  accent-color: #2563eb;
}

.user-import-mail-option span {
  display: flex;
  flex-direction: column;
}

.user-import-mail-option strong {
  color: #334155;
  font-size: 0.875rem;
}

.user-import-mail-option small {
  margin-top: 0.15rem;
  color: #64748b;
  font-size: 0.75rem;
}

.user-import-errors {
  padding: 0.9rem 1rem;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.8rem;
}

.user-import-errors > div {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.user-import-errors ul {
  max-height: 9rem;
  margin-top: 0.65rem;
  padding-left: 1.25rem;
  overflow-y: auto;
  line-height: 1.5;
  list-style: disc;
}

.user-import-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.user-import-cancel,
.user-import-submit {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 700;
}

.user-import-cancel {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #475569;
}

.user-import-cancel:hover:not(:disabled) {
  background: #f1f5f9;
}

.user-import-submit {
  background: #2563eb;
  color: #fff;
}

.user-import-submit:hover:not(:disabled) {
  background: #1d4ed8;
}

.user-import-modal button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.user-import-modal.is-loading,
.user-import-modal.is-loading button:disabled,
.user-import-modal.is-loading .user-import-file,
.user-import-modal.is-loading .user-import-file__input {
  cursor: wait;
}

.user-import-submit:disabled {
  background: #bfdbfe;
}

.user-import-modal button:focus-visible,
.user-import-mail-option input:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

@media (max-width: 640px) {
  .user-import-section-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .user-import-template-button {
    align-self: flex-start;
  }

  .user-import-file {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .user-import-file__copy {
    min-width: calc(100% - 3.75rem);
  }

  .user-import-file__action {
    margin-left: 3.6rem;
  }

  .user-import-modal__footer {
    align-items: stretch;
    flex-direction: column-reverse;
  }
}
</style>
